import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ApiContext } from '@api/api';
import {
  ItemStatusEnum,
  JobStatusEnum,
  JobTypeEnum,
  MultiduplicationV3ItemsFilter,
  MultiduplicationV3JobDetailOutDTO,
  MultiduplicationV3JobItemOutDTO,
  StateFilterEnum,
  Pageable,
  PermissionTypesEnum,
  PhaseEnum,
} from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import useNavigation from '@hooks/useNavigation.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { Link, useSearchParams } from 'react-router-dom';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COBanner from '@components/FigmaComponents/COBanner.tsx';
import { COBannerVariant } from '@components/FigmaComponents/COBannerVariant.ts';
import COText from '@app/pages/COText/COText.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import SvgOK from '@icons/check-circle.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import SvgOrder from '@icons/order.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgReset from '@icons/reset-filled.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { mapFromAPIToDateShort } from '@utils/date.tsx';
import { isNotNullOrUndefined, useLocalStorage } from '@utils/utils';
import { formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';
import { useJobChannel } from '@hooks/useJobs/useJobChannel.ts';
import { InfoTagEnum, UiMode } from './multiduplicationV3Table.ts';
import MultiduplicationV3RowDetail from './MultiduplicationV3RowDetail.tsx';
import DuplicationStepper from './DuplicationStepper.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';

interface ComponentProps {
  jobId: string;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];
const RUNNING: JobStatusEnum[] = [JobStatusEnum.PENDING, JobStatusEnum.RUNNING];

const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
  (acc, key) => ({ ...acc, [key]: false }),
  {} as Record<InfoTagEnum, boolean>,
);

/**
 * Multiduplication v3 draft/job detail. Job-level data from one GET (+ reconciliation GET on
 * finish, live counters via the per-job Centrifugo channel); the row tables are classic
 * RingilDataTables paged/sorted/filtered on the BE, reloaded (debounced) on push hints.
 * Finish page (Figma): failed section (WITH_ERROR) + "Zopakovat duplikaci neúspěšných" +
 * successful section (OK).
 */
const MultiduplicationV3JobDetail: React.FC<ComponentProps> = ({ jobId }) => {
  const {
    getMultiduplicationV3JobDetail,
    getMultiduplicationV3JobItems,
    keepMultiduplicationV3Items,
    deleteMultiduplicationV3Item,
    executeMultiduplicationV3Job,
    revalidateMultiduplicationV3Job,
    deleteMultiduplicationV3Job,
    cancelAsyncJob,
    archiveJob,
    retryJob,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  // Leaving a draft that still exists should land the user where they came in from; anything other
  // than an explicit EXPEDITIONS marker (direct link, bookmark, reload) falls back to the jobs
  // centre, where every job is findable.
  const [searchParams] = useSearchParams();
  const cameFromExpeditions = searchParams.get('jobReferrer') === JobReferrer.EXPEDITIONS;
  const leaveDraftUrl = () => (cameFromExpeditions ? nav.urlFunctions.createCustomerExpeditionList() : nav.urlFunctions.createJobList());
  const [detail, setDetail] = useState<MultiduplicationV3JobDetailOutDTO>();
  const abortRef = useRef<AbortController | null>(null);
  const tableRef = useRef<{
    reloadData: () => void;
    selectedObjects: MultiduplicationV3JobItemOutDTO[];
    clear: () => void;
    applyFilterValue: (property: string, value: any) => void;
  }>(null);
  const failedTableRef = useRef<{ reloadData: () => void; selectedObjects: MultiduplicationV3JobItemOutDTO[]; clear: () => void }>(null);
  const doneTableRef = useRef<{ reloadData: () => void; selectedObjects: MultiduplicationV3JobItemOutDTO[]; clear: () => void }>(null);
  const reloadTimer = useRef<number | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [executeConfirmVisible, setExecuteConfirmVisible] = useState(false);
  // Key of the mutating action in flight (RD-2954): 'retry' | 'execute' | 'revalidate' | 'cancel' |
  // 'archive' | 'keep-mass' | `keep-${rowId}` | `delete-${rowId}`. The clicked button spins
  // (RingilButton swallows clicks while `loading`) and every other action button greys out, so neither
  // a double click nor a parallel action sends a second request — that used to reach the BE after the
  // first had already moved the job on and came back as a 409.
  // The key is cleared only once the detail GET that follows the mutation has landed: on a fast
  // (local) backend the POST can return before the second click of a double click, and until the
  // refetched status re-renders the page the old button is still there and would fire again.
  const [action, setAction] = useState<string | null>(null);
  const busy = (key: string) => ({ loading: action === key, disabled: action != null && action !== key });

  // Jobs-center viewers (JOBS_READ/RW) can open the draft; acting on it needs the dedicated permission.
  const editFlag = useSecuredFlag('multiduplication-edit', {
    kind: 'action',
    description: 'Create/edit/execute bulk duplication drafts',
    permissions: [PermissionTypesEnum.PERMISSION_MULTIDUPLICATION_V3],
  });
  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>(
    'MULTIDUPLICATION_V3_EXPANDED_INFO_TAGS',
    initialExpandedInfoTags,
  );
  const [expandedLabels, setExpandedLabels] = useLocalStorage('MULTIDUPLICATION_V3_EXPANDED_LABELS', false);
  const toggleInfoTag = (key: InfoTagEnum) => setExpandedInfoTags({ ...expandedInfoTags, [key]: !expandedInfoTags[key] });
  const toggleAllInfoTags = () => {
    const expanded = !expandedInfoTags[InfoTagEnum.ALL];
    setExpandedInfoTags(Object.values(InfoTagEnum).reduce((acc, key) => ({ ...acc, [key]: expanded }), {} as Record<InfoTagEnum, boolean>));
  };
  const toggleLabels = () => setExpandedLabels(!expandedLabels);

  const load = (onLoaded?: () => void) => {
    // Effect-local controller: a jobId switch must not let the previous job's response through.
    const ac = abortRef.current;
    getMultiduplicationV3JobDetail(jobId, d => {
      if (ac?.signal.aborted) return;
      setDetail(d);
      onLoaded?.();
    });
  };

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    load();
    return () => ac.abort();
  }, [jobId]);

  useEffect(() => () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
  }, []);

  const debouncedTableReload = () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => {
      tableRef.current?.reloadData();
      failedTableRef.current?.reloadData();
      doneTableRef.current?.reloadData();
    }, 400);
  };

  const reloadAll = (onLoaded?: () => void) => {
    load(onLoaded);
    debouncedTableReload();
  };

  const { live } = useJobChannel(jobId, {
    onProgress: e => {
      setDetail(prev => {
        if (prev == null) return prev;
        return {
          ...prev,
          job: {
            ...prev.job,
            jobStatus: (e.status as JobStatusEnum) ?? prev.job.jobStatus,
            processedCount: e.processed ?? prev.job.processedCount,
            successCount: e.success ?? prev.job.successCount,
            failedCount: e.failed ?? prev.job.failedCount,
            totalCount: e.total ?? prev.job.totalCount,
          },
        };
      });
      debouncedTableReload();
    },
    onFinished: () => reloadAll(),
    onDraftChanged: e => {
      if (e.reason === 'JOB_DELETED') {
        showToastMessage(tr('MultiduplicationV3.deleted', 'Seznam byl smazán'), '', 'info');
        nav.navigate(leaveDraftUrl());
        return;
      }
      reloadAll();
    },
  });

  const status = detail?.job?.jobStatus;
  const phase = detail?.phase;
  const mode: UiMode =
    status == null || TERMINAL.includes(status)
      ? 'TERMINAL'
      : status === JobStatusEnum.DRAFT
        ? 'DRAFT'
        : phase === PhaseEnum.EXECUTE
          ? 'EXECUTING'
          : 'VALIDATING';
  const isRunning = status != null && RUNNING.includes(status);
  const isTerminal = status != null && TERMINAL.includes(status);
  const isDraftPhase = mode === 'DRAFT' || mode === 'VALIDATING';

  // "After check, show only the problems": when the DRAFT settles with issue rows, preselect the
  // WITH_ERROR filter — once per settle (a live phase transition re-arms it), so the user can
  // still clear the filter without it snapping back. The finish page uses fixed-filter tables.
  const appliedIssueFilter = useRef(false);
  const prevModeRef = useRef<UiMode | null>(null);
  useEffect(() => {
    if (detail == null) return;
    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;
    if (mode !== 'DRAFT') return;
    if (prevMode === 'VALIDATING') appliedIssueFilter.current = false;
    if (appliedIssueFilter.current) return;
    appliedIssueFilter.current = true;
    if (detail.itemsWithIssues > 0) {
      tableRef.current?.applyFilterValue('stateFilter', StateFilterEnum.WITH_ERROR);
    }
  }, [detail == null, mode, detail?.itemsWithIssues]);

  // Poll fallback: running -> 15s (subscription down) / 60s (insurance); DRAFT + down -> 60s
  useEffect(() => {
    if (detail == null) return;
    let intervalMs: number | null = null;
    if (isRunning) intervalMs = live ? 60000 : 15000;
    else if (status === JobStatusEnum.DRAFT && !live) intervalMs = 60000;
    if (intervalMs == null) return;
    const id = setInterval(reloadAll, intervalMs);
    return () => clearInterval(id);
  }, [jobId, isRunning, status, live, detail == null]);

  const hasIssues = (row: MultiduplicationV3JobItemOutDTO) =>
    row.itemStatus === ItemStatusEnum.ERROR || (row.openingCollisions ?? 0) > 0 || (row.reservationConflicts ?? 0) > 0;

  const dateRange = (row: MultiduplicationV3JobItemOutDTO) =>
    row.dateFrom != null && row.dateTo != null
      ? row.dateFrom === row.dateTo
        ? mapFromAPIToDateShort(row.dateFrom)
        : `${mapFromAPIToDateShort(row.dateFrom)} – ${mapFromAPIToDateShort(row.dateTo)}`
      : row.targetDate != null
        ? mapFromAPIToDateShort(row.targetDate)
        : null;

  // `rowId` null = mass action from the table toolbar, otherwise the single row's Ponechat button.
  const keepRows = (itemIds: string[], rowId: string | null) => {
    setAction(rowId == null ? 'keep-mass' : `keep-${rowId}`);
    keepMultiduplicationV3Items(
      jobId,
      { itemIds, kept: true },
      () => {
        tableRef.current?.clear();
        failedTableRef.current?.clear();
        reloadAll();
      },
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const deleteRow = (itemId: string, rowId: string) => {
    setAction(`delete-${rowId}`);
    deleteMultiduplicationV3Item(
      jobId,
      itemId,
      () => reloadAll(),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const retry = () => {
    setAction('retry');
    retryJob(
      jobId,
      () => reloadAll(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const revalidate = () => {
    setAction('revalidate');
    revalidateMultiduplicationV3Job(
      jobId,
      () => reloadAll(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const cancel = () => {
    setAction('cancel');
    cancelAsyncJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  const deleteDraft = () => {
    setAction('archive');
    deleteMultiduplicationV3Job(jobId, () => nav.navigate(leaveDraftUrl()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  // Row actions (keep/delete) live in the tables, which reload 400 ms after the detail, so their key
  // is released only once fresh rows are in — until then the old row is still on screen.
  const rowsLoaded = () => setAction(prev => (prev != null && (prev.startsWith('keep-') || prev.startsWith('delete-')) ? null : prev));

  const loadDataApi = (filter: MultiduplicationV3ItemsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<MultiduplicationV3JobItemOutDTO>) => void) => {
    getMultiduplicationV3JobItems(jobId, filter, pageable, data => {
      rowsLoaded();
      onSuccess(data);
    });
  };
  // Finish-page sections use fixed filters regardless of any persisted filter state
  const loadFailedApi = (_: MultiduplicationV3ItemsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<MultiduplicationV3JobItemOutDTO>) => void) => {
    getMultiduplicationV3JobItems(jobId, { stateFilter: StateFilterEnum.WITH_ERROR }, pageable, data => {
      rowsLoaded();
      onSuccess(data);
    });
  };
  const loadDoneApi = (_: MultiduplicationV3ItemsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<MultiduplicationV3JobItemOutDTO>) => void) => {
    getMultiduplicationV3JobItems(jobId, { stateFilter: StateFilterEnum.OK }, pageable, data => onSuccess(data));
  };

  const rowExpandable = (row: MultiduplicationV3JobItemOutDTO) =>
    isDraftPhase ||
    (row.execution != null && (row.execution.locations ?? []).some(l => l.reservationCreated != null)) ||
    (row.itemStatus === ItemStatusEnum.ERROR && row.targetDate != null);

  const renderRouteColumn = (row: MultiduplicationV3JobItemOutDTO, toggleExpandedRow?: (row: MultiduplicationV3JobItemOutDTO) => void) => {
    const route = row.routeSummary ?? [];
    const stopCount = Math.max(route.length - 2, 0);
    // A draft row carries a verdict too, not just a finished one: the check that just ran either
    // found something on it or did not, which is what the designs show on the draft step.
    const statusIcon = isTerminal ? (
      row.itemStatus === ItemStatusEnum.ERROR || row.execution?.someReservationFailed ? (
        <SvgAlert className={'text-complementary-error shrink-0'} />
      ) : row.itemStatus === ItemStatusEnum.DONE ? (
        <SvgOK className={'text-complementary-success shrink-0'} />
      ) : null
    ) : hasIssues(row) ? (
      <SvgWarning className={'text-complementary-warning shrink-0'} />
    ) : (
      <SvgOK className={'text-complementary-success shrink-0'} />
    );
    return (
      <div className={'flex flex-row gap-2 items-start'}>
        {/* Centred on the ID badge's line rather than flush to the top of the cell: the badge is 24px
            (14px text, py-1, 1px border) and the glyph is 16px, so top-aligning it reads ~4px high.
            Rendered only when there is an icon, so an iconless row gets no empty flex item + gap. */}
        {statusIcon != null && <span className={'flex h-6 shrink-0 items-center'}>{statusIcon}</span>}
        <div className={'flex flex-col gap-1'}>
          <COBadge variant={'outlined'} type={'neutral'}>
            <span className={'text-content-secondary'}>{tr('MultiduplicationJobTable.rowId', 'ID:')}</span>
            &nbsp;{row.sortOrder + 1}
            {row.targetDate != null && <>&nbsp;·&nbsp;{mapFromAPIToDateShort(row.targetDate)}</>}
          </COBadge>
          {/* The row's own state, so the action column holds only actions and stops changing size
              when a row is kept. Yellow rather than green: keeping accepts the collision, it does
              not resolve it — the duplicate is still created with the conflict. */}
          {row.kept && (
            <COBadge variant={'filled'} type={'warning'}>
              {tr('MultiduplicationV3.kept', 'Ponecháno')}
            </COBadge>
          )}
          {route.length > 0 ? (
            <>
              <span className={'text-content-primary'}>{route[0].locationName}</span>
              {route.length > 1 && <span className={'text-content-primary'}>{route[route.length - 1].locationName}</span>}
              {stopCount > 0 && (
                <span className={'text-sm text-content-tertiary'}>
                  {tr('MultiduplicationV3.intermediateStops', '+ {{count}} mezizastávky', { count: stopCount }, stopCount)}
                </span>
              )}
            </>
          ) : (
            <span className={'text-content-secondary'}>{row.originalApplicationId ?? row.expeditionId}</span>
          )}
          {rowExpandable(row) && (
            <span
              className={'flex flex-row gap-1 items-center cursor-pointer text-content-brand'}
              onClick={() => toggleExpandedRow?.(row)}
              data-testid={`multiduplication-v3-row-${row.sortOrder}-details`}
            >
              {tr('MultiduplicationV3.details', 'Podrobnosti')}
              <SvgChevron className={'text-content-brand'} />
            </span>
          )}
        </div>
      </div>
    );
  };

  const itemErrorText = (row: MultiduplicationV3JobItemOutDTO) => formatJobItemError(row.error, row.errorMessage, tr);

  const renderOpeningColumn = (row: MultiduplicationV3JobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin'} />;
    if (row.itemStatus === ItemStatusEnum.PENDING) return <COText label={tr('MultiduplicationV3.waiting', 'Čeká')} />;
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      return (
        <span className={'flex flex-row gap-1 items-center'}>
          <SvgAlert className="text-complementary-error shrink-0" />
          <span className={'text-content-tertiary text-sm'}>{itemErrorText(row)}</span>
        </span>
      );
    }
    if (row.openingCollisions == null) {
      // Post-execution rows have no validation data anymore
      return isTerminal ? (
        <span className={'flex flex-row gap-1 items-center'}>
          <SvgOK className="text-complementary-success shrink-0" />
          <COText label={tr('MultiduplicationV3.allOk', 'Vše v pořádku')} />
        </span>
      ) : null;
    }
    return (
      <div className={'flex flex-col gap-1'}>
        {row.openingCollisions === 0 ? (
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgOK className="text-complementary-success shrink-0" />
            <COText label={tr('MultiduplicationV3.allOk', 'Vše v pořádku')} />
          </span>
        ) : (
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgWarning className="text-complementary-warning shrink-0" />
            <COText label={`${tr('MultiduplicationV3.collision', 'Kolize')} (${row.openingCollisions})`} />
          </span>
        )}
        {dateRange(row) != null && <span className={'text-sm text-content-tertiary'}>{dateRange(row)}</span>}
      </div>
    );
  };

  const renderReservationColumn = (row: MultiduplicationV3JobItemOutDTO) => {
    if (row.reservationConflicts == null) return null;
    return (
      <div className={'flex flex-col gap-1'}>
        {row.reservationConflicts === 0 ? (
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgOK className="text-complementary-success shrink-0" />
            <COText label={tr('MultiduplicationV3.allOk', 'Vše v pořádku')} />
          </span>
        ) : (
          <span className={'flex flex-row gap-1 items-center text-complementary-error'}>
            <SvgAlert className="shrink-0" />
            {tr('MultiduplicationV3.occupied', 'Obsazeno')} ({row.reservationConflicts})
          </span>
        )}
        {dateRange(row) != null && <span className={'text-sm text-content-tertiary'}>{dateRange(row)}</span>}
      </div>
    );
  };

  // Rezervace column after execution: were the copied reservations created?
  const renderExecutionReservationColumn = (row: MultiduplicationV3JobItemOutDTO) => {
    if (row.execution == null) return null;
    const failedCount = (row.execution.locations ?? []).filter(l => l.reservationCreated === false).length;
    const hasAny = (row.execution.locations ?? []).some(l => l.reservationCreated != null);
    return (
      <div className={'flex flex-col gap-1 items-start'}>
        {row.execution.someReservationFailed || failedCount > 0 ? (
          <span className={'flex flex-row gap-1 items-center text-complementary-error'}>
            <SvgAlert className="shrink-0" />
            {tr('MultiduplicationV3.reservationsFailed', 'Nevytvořeno')} ({Math.max(failedCount, 1)})
          </span>
        ) : hasAny ? (
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgOK className="text-complementary-success shrink-0" />
            <COText label={tr('MultiduplicationV3.allOk', 'Vše v pořádku')} />
          </span>
        ) : (
          <span className={'text-content-tertiary'}>—</span>
        )}
        {isNotNullOrUndefined(row.execution.expeditionId) && (
          <Link className={'text-sm'} to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(row.execution.expeditionId))}>
            {row.execution.expeditionApplicationId}
          </Link>
        )}
      </div>
    );
  };

  const renderCreatedColumn = (row: MultiduplicationV3JobItemOutDTO) => {
    if (row.execution != null && isNotNullOrUndefined(row.execution.expeditionId)) {
      return (
        <div className={'flex flex-row gap-2 items-center'}>
          <Link className={'text-content-brand font-medium'} to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(row.execution.expeditionId))}>
            {row.execution.expeditionApplicationId}
          </Link>
          {row.execution.someReservationFailed && (
            <span title={tr('MultiduplicationV3.reservationFailed', 'Některé rezervace se nepodařilo vytvořit')}>
              <SvgWarning className="text-complementary-warning" />
            </span>
          )}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      return (
        <div className={'flex flex-col gap-1'}>
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgAlert className="text-complementary-error shrink-0" />
            <COText label={tr('MultiduplicationV3.notCreated', 'Nevytvořeno')} />
          </span>
          {isNotNullOrUndefined(itemErrorText(row)) && <span className={'text-content-tertiary text-sm'}>{itemErrorText(row)}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin'} />;
    if (row.itemStatus === ItemStatusEnum.SKIPPED) return <span className={'text-content-tertiary'}>{tr('MultiduplicationV3.skipped', 'Přeskočeno')}</span>;
    return <COText label={tr('MultiduplicationV3.waiting', 'Čeká')} />;
  };

  const renderSourceColumn = (row: MultiduplicationV3JobItemOutDTO) => (
    <div className={'flex flex-col gap-1'}>
      <Link to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(row.expeditionId))}>
        {row.originalApplicationId ?? tr('MultiduplicationV3.sourceExpedition', 'Výchozí přeprava')}
      </Link>
      {(row.labels ?? []).length > 0 && <ExpandableLabelWrapper labels={row.labels} isExpanded={expandedLabels} onToggle={toggleLabels} />}
    </div>
  );

  const renderInfoTagsColumn = (row: MultiduplicationV3JobItemOutDTO) => {
    const expeditionType = row.expeditionType == null ? null : tr(`global.expeditionTypeShort${row.expeditionType}`, `${row.expeditionType}`);
    return (
      <div className={'flex flex-wrap gap-2 items-start'}>
        {(expeditionType != null || isNotNullOrUndefined(row.responsiblePerson) || (row.orderCount ?? 0) > 0) && (
          <ExpandableInfoAllTag
            id={`all_${row.id}`}
            onClick={toggleAllInfoTags}
            tooltipText={expandedInfoTags[InfoTagEnum.ALL] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', expandedInfoTags[InfoTagEnum.ALL] && 'rotate-180')} />}
          />
        )}
        {expeditionType != null && (
          <ExpandableInfoTag id={`type_${row.id}`} expanded={expandedInfoTags[InfoTagEnum.TYPE]} onClick={() => toggleInfoTag(InfoTagEnum.TYPE)} iconText={expeditionType} />
        )}
        {isNotNullOrUndefined(row.responsiblePerson) && (
          <ExpandableInfoTag
            id={`responsible_${row.id}`}
            expanded={expandedInfoTags[InfoTagEnum.RESPONSIBLE_PERSON]}
            onClick={() => toggleInfoTag(InfoTagEnum.RESPONSIBLE_PERSON)}
            label={tr('MultiduplicationV3.responsiblePersonLabel', 'Odpovědná osoba: ')}
            text={row.responsiblePerson}
            icon={<SvgUser />}
          />
        )}
        {(row.orderCount ?? 0) > 0 && (
          <ExpandableInfoTag
            id={`orders_${row.id}`}
            expanded={expandedInfoTags[InfoTagEnum.ORDERS]}
            onClick={() => toggleInfoTag(InfoTagEnum.ORDERS)}
            label={tr('AllExpedition.ordersTagLabel', 'Orders: ')}
            text={`${row.orderCount}`}
            icon={<SvgOrder />}
            count={row.orderCount}
          />
        )}
      </div>
    );
  };

  const renderActionsColumn = (row: MultiduplicationV3JobItemOutDTO, toggleExpandedRow?: (row: MultiduplicationV3JobItemOutDTO) => void) => {
    if (mode !== 'DRAFT' || !editFlag.granted) return null;
    return (
      <div className={'flex flex-col gap-2 items-end'}>
        {(row.reservationConflicts ?? 0) > 0 && (
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr('MultiduplicationV3.findSlot', 'Najít termín')}
            dataTestId={`multiduplication-v3-row-${row.sortOrder}-findSlot`}
            onClick={() => toggleExpandedRow?.(row)}
          />
        )}
        {hasIssues(row) && !row.kept && (
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr('MultiduplicationV3.keep', 'Ponechat')}
            dataTestId={`multiduplication-v3-row-${row.sortOrder}-keep`}
            onClick={() => keepRows([row.id], row.id)}
            {...busy(`keep-${row.id}`)}
          />
        )}
        <RingilButton
          mode={'outlined-rectangle'}
          icon={<SvgDelete />}
          label={tr('MultiduplicationV3.delete', 'Smazat')}
          dataTestId={`multiduplication-v3-row-${row.sortOrder}-delete`}
          onClick={() => deleteRow(row.itemId, row.id)}
          {...busy(`delete-${row.id}`)}
        />
      </div>
    );
  };

  // Finish page failed section: fix the timeslot of a not-created row + Ponechat (acknowledge)
  const renderFailedActionsColumn = (row: MultiduplicationV3JobItemOutDTO, toggleExpandedRow?: (row: MultiduplicationV3JobItemOutDTO) => void) => {
    if (!editFlag.granted) return null;
    return (
      <div className={'flex flex-col gap-2 items-end'}>
        {row.itemStatus === ItemStatusEnum.ERROR && row.targetDate != null && (
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr('MultiduplicationV3.findSlot', 'Najít termín')}
            dataTestId={`multiduplication-v3-row-${row.sortOrder}-findSlot`}
            onClick={() => toggleExpandedRow?.(row)}
          />
        )}
        {!row.kept && (
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr('MultiduplicationV3.keep', 'Ponechat')}
            dataTestId={`multiduplication-v3-row-${row.sortOrder}-keep`}
            onClick={() => keepRows([row.id], row.id)}
            {...busy(`keep-${row.id}`)}
          />
        )}
      </div>
    );
  };

  const draftColumns: ColumnConfig<MultiduplicationV3JobItemOutDTO>[] = [
    { id: 'route', header: 'MultiduplicationV3.table.route', headerDefault: 'Trasa', width: '22%', order: 1, render: renderRouteColumn },
    { id: 'opening', header: 'MultiduplicationV3.table.openingHours', headerDefault: 'Otevírací doba', width: '15%', order: 2, render: renderOpeningColumn },
    { id: 'reservation', header: 'MultiduplicationV3.table.reservation', headerDefault: 'Rezervace', width: '15%', order: 3, render: renderReservationColumn },
    { id: 'duplicatedFrom', header: 'MultiduplicationV3.table.duplicatedFrom', headerDefault: 'Zduplikováno z', width: '16%', order: 4, render: renderSourceColumn },
    { id: 'additionalInfo', header: 'MultiduplicationV3.table.additionalInfo', headerDefault: 'Další informace', width: '18%', order: 5, render: renderInfoTagsColumn },
    { id: 'actions', header: 'MultiduplicationV3.table.whatToDo', headerDefault: 'Co dělat', width: '14%', order: 6, render: renderActionsColumn },
  ];

  const failedColumns: ColumnConfig<MultiduplicationV3JobItemOutDTO>[] = [
    { id: 'route', header: 'MultiduplicationV3.table.statusId', headerDefault: 'Status | ID', width: '22%', order: 1, render: renderRouteColumn },
    { id: 'opening', header: 'MultiduplicationV3.table.openingHours', headerDefault: 'Otevírací doba', width: '17%', order: 2, render: renderOpeningColumn },
    { id: 'reservation', header: 'MultiduplicationV3.table.reservation', headerDefault: 'Rezervace', width: '15%', order: 3, render: renderExecutionReservationColumn },
    { id: 'duplicatedFrom', header: 'MultiduplicationV3.table.duplicatedFrom', headerDefault: 'Zduplikováno z', width: '16%', order: 4, render: renderSourceColumn },
    { id: 'additionalInfo', header: 'MultiduplicationV3.table.additionalInfo', headerDefault: 'Další informace', width: '18%', order: 5, render: renderInfoTagsColumn },
    { id: 'actions', header: '', headerDefault: '', width: '12%', order: 6, render: renderFailedActionsColumn },
  ];

  const doneColumns: ColumnConfig<MultiduplicationV3JobItemOutDTO>[] = [
    { id: 'route', header: 'MultiduplicationV3.table.statusId', headerDefault: 'Status | ID', width: '28%', order: 1, render: renderRouteColumn },
    { id: 'created', header: 'MultiduplicationV3.table.created', headerDefault: 'Vytvořená přeprava', width: '28%', order: 2, render: renderCreatedColumn },
    { id: 'duplicatedFrom', header: 'MultiduplicationV3.table.duplicatedFrom', headerDefault: 'Zduplikováno z', width: '22%', order: 3, render: renderSourceColumn },
    { id: 'additionalInfo', header: 'MultiduplicationV3.table.additionalInfo', headerDefault: 'Další informace', width: '22%', order: 4, render: renderInfoTagsColumn },
  ];

  const sortColumn = (order: number): ColumnConfig<MultiduplicationV3JobItemOutDTO> => ({
    id: 'sortOrder',
    header: 'MultiduplicationV3.sortId',
    headerDefault: 'ID Trasy',
    field: 'sortOrder',
    sortable: true,
    isDefaultSort: true,
    defaultSortDirection: 'ASC',
    hidden: true,
    order,
    render: () => null,
  });

  const stateLabel = (state: StateFilterEnum) =>
    ({
      [StateFilterEnum.WITH_ERROR]: tr('MultiduplicationV3.state.WITH_ERROR', 'Jen kolize a chyby'),
      [StateFilterEnum.OK]: tr('MultiduplicationV3.state.OK', 'V pořádku'),
    })[state];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('MultiduplicationV3.filterState', 'Status'),
      label: tr('MultiduplicationV3.filterState', 'Status'),
      value: ['stateFilter'],
      options: Object.values(StateFilterEnum).map(state => ({ label: stateLabel(state), value: state, active: true })),
      dataTestId: 'multiduplication-v3-filter-state',
    },
  ];

  const rowExpansionTemplate = (row: MultiduplicationV3JobItemOutDTO) => (
    <MultiduplicationV3RowDetail
      jobId={jobId}
      item={row}
      canEdit={editFlag.granted && (mode === 'DRAFT' || (isTerminal && row.itemStatus === ItemStatusEnum.ERROR))}
      onSaved={() => reloadAll()}
    />
  );

  if (detail == null) {
    return <SvgLoader className={'animate-spin'} />;
  }

  const job = detail.job;
  const progressPct = job.totalCount > 0 ? Math.round((job.processedCount / job.totalCount) * 100) : 0;
  const isDone = status === JobStatusEnum.DONE;
  /** Until the job has produced its first row there is nothing to tabulate — progress is the whole story. */
  const hasItems = (detail.itemsTotal ?? 0) > 0;
  const okCount = Math.max((job.totalCount ?? 0) - detail.itemsWithIssues, 0);
  const stepperOutcome = !isTerminal ? undefined : status === JobStatusEnum.ERROR ? 'error' : detail.itemsWithIssues > 0 ? 'warning' : 'ok';
  const title = `${
    isDone
      ? tr('MultiduplicationV3.resultTitle', 'Výsledek duplikace přepravy')
      : status === JobStatusEnum.CANCELLED
        ? tr('JobDetail.task', 'Úkol')
        : tr('MultiduplicationV3.title', 'Duplikovat přepravy')
  } ${job.applicationId ?? ''}`;

  const runExecute = () => {
    setAction('execute');
    executeMultiduplicationV3Job(
      jobId,
      () => reloadAll(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  // The designs grey the primary button out while issues remain, but that is only safe if the
  // backend drops kept rows from itemsWithIssues — otherwise a single collision would make the
  // draft impossible to execute. Until that is confirmed, ask instead of blocking.
  const execute = () => {
    if (detail.itemsWithIssues > 0) {
      setExecuteConfirmVisible(true);
      return;
    }
    runExecute();
  };

  const failedTable = (
    <RingilDataTable
      ref={failedTableRef}
      columnConfig={[...failedColumns, sortColumn(10)]}
      loadDataFromApi={loadFailedApi}
      filterName={'multiduplication_v3_failed'}
      massActionConfig={[
        {
          label: tr('MultiduplicationV3.keep', 'Ponechat'),
          secured: () => ({ id: 'mass-action-multiduplication-v3-keep-failed', label: 'Keep failed rows', description: 'Keep the selected failed rows of a bulk duplication', visible: editFlag }),
          disabled: () => action != null,
          onClick: () => {
            const ids = (failedTableRef.current?.selectedObjects ?? []).map(r => r.id);
            if (ids.length > 0) keepRows(ids, null);
          },
        },
      ]}
      rowExpansionTemplate={rowExpansionTemplate}
    />
  );

  const doneTable = (
    <RingilDataTable
      ref={doneTableRef}
      columnConfig={[...doneColumns, sortColumn(10)]}
      loadDataFromApi={loadDoneApi}
      filterName={'multiduplication_v3_done'}
      rowExpansionTemplate={rowExpansionTemplate}
    />
  );

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <DuplicationStepper activeStep={isDraftPhase ? 2 : 3} outcome={stepperOutcome} />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${JobTypeEnum.MULTIDUPLICATION_V3}`, 'Duplikace')}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={job.createdAt}
        buttons={
          mode === 'DRAFT' && editFlag.granted ? (
            <RingilButton
              mode={'outlined-rectangle'}
              icon={<SvgReset />}
              label={tr('MultiduplicationV3.refresh', 'Znovu zkontrolovat')}
              onClick={revalidate}
              {...busy('revalidate')}
            />
          ) : undefined
        }
        onCancel={isRunning ? cancel : undefined}
        onArchive={mode === 'DRAFT' && editFlag.granted ? () => setDeleteConfirmVisible(true) : isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      {mode === 'DRAFT' && detail.itemsWithIssues > 0 && (
        <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('MultiduplicationV3.issuesStrip', 'V {{total}} přepravách máte {{issues}} kolize nebo chyby, zkontrolujte a schvalte přepravy k duplikaci.', {
            total: detail.itemsTotal,
            issues: detail.itemsWithIssues,
          })}
        </COInlineMessage>
      )}

      {/* Banner and card are one block in the design: the heading sits in the yellow banner and the
          card below carries nothing but the spinner and the percentage. */}
      {isRunning && (
        <div className={'flex flex-col'}>
          <COBanner
            variant={COBannerVariant.Warning}
            className={'rounded-t'}
            label={mode === 'EXECUTING' ? tr('MultiduplicationV3.executing', 'Vytvářím přepravy') : tr('MultiduplicationV3.validating', 'Kontroluji přepravy')}
          />
          <div className={'flex flex-col items-center gap-4 rounded-b bg-white py-12'}>
            {/* SvgLoader paints a fixed grey stroke, so the brand colour has to come from CSS. */}
            <SvgLoader className={'h-[29px] w-[29px] animate-spin [&_path]:stroke-content-brand'} />
            <span className={'text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct: progressPct })}</span>
          </div>
        </div>
      )}

      {isDone && (
        <div className={'flex flex-row items-center justify-center gap-3 rounded bg-white p-6'}>
          <SvgRobot width={32} height={32} />
          {detail.itemsWithIssues > 0 ? (
            <>
              <SvgWarning className={'text-complementary-warning shrink-0'} />
              <span>
                {tr('MultiduplicationV3.robotPartial', '{{failed}} přeprav se nepodařilo zduplikovat, dalších {{ok}} přeprav bylo úspěšně zduplikováno.', {
                  failed: detail.itemsWithIssues,
                  ok: okCount,
                })}
              </span>
            </>
          ) : (
            <>
              <SvgOK className={'text-complementary-success shrink-0'} />
              <span>{tr('MultiduplicationV3.robotAllOk', 'Všech {{count}} přeprav bylo úspěšně zduplikovaných.', { count: job.totalCount })}</span>
            </>
          )}
        </div>
      )}

      {/* Same banner-joined-to-card construction as the running state, in the error palette. Without
          the retry permission there is no card, so the banner keeps all four corners rounded. */}
      {status === JobStatusEnum.ERROR && (
        <div className={'flex flex-col'}>
          <COBanner
            variant={COBannerVariant.Error}
            className={jobsRwFlag.granted ? 'rounded-t' : 'rounded'}
            label={
              <span>
                <strong>{tr('MultiduplicationV3.jobFailedCount', 'V duplikaci {{count}} přeprav', { count: job.totalCount })}</strong>{' '}
                {tr('MultiduplicationV3.jobFailedText', 'se vyskytla chyba. Nepodařilo se jí dokončit.')}
              </span>
            }
          />
          {jobsRwFlag.granted && (
            <div className={'flex flex-col items-center rounded-b bg-white py-12'}>
              <RingilButton
                mode={'filled-rectangle'}
                size={'big'}
                icon={<SvgReset />}
                label={tr('JobList.retry', 'Zkusit znovu')}
                dataTestId={'multiduplication-v3-retry'}
                onClick={retry}
                {...busy('retry')}
              />
            </div>
          )}
        </div>
      )}
      {status === JobStatusEnum.CANCELLED && (
        <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('MultiduplicationV3.cancelled', 'Úloha byla zrušena.')}
        </COInlineMessage>
      )}

      {isDraftPhase || mode === 'EXECUTING' ? (
        hasItems && (
          <RingilDataTable
            ref={tableRef}
            columnConfig={[...draftColumns, sortColumn(10)]}
            filtersConf={filtersConf}
            loadDataFromApi={loadDataApi}
            filterName={'multiduplication_v3_items'}
            // Keeping is the only mass action, and only a row with something wrong can be kept, so
            // only those get a checkbox — a clean row shows its status icon in that position, as the
            // designs do. Also keeps clean rows out of select-all. Revisit if a mass delete lands:
            // deleting a clean row is legitimate, so that would make every row selectable again.
            isRowSelectable={hasIssues}
            // Only the issues filter gets a tailored empty state — it is pre-applied when the check
            // finds problems, so landing on it with nothing to show means the draft is clean rather
            // than that the user filtered badly. Any other empty result keeps the generic message.
            emptyMessage={f =>
              f?.stateFilter === StateFilterEnum.WITH_ERROR ? (
                <span>
                  <strong>{tr('MultiduplicationV3.noIssuesCount', 'V {{count}} přepravách', { count: detail.itemsTotal })}</strong>{' '}
                  {tr('MultiduplicationV3.noIssuesText', 'nemáte žádnou kolizi nebo chybu, která by bránila duplikaci.')}{' '}
                  <span className={clickableTextClass} onClick={() => tableRef.current?.applyFilterValue('stateFilter', null)}>
                    {tr('MultiduplicationV3.showAllItems', 'Zobrazit přepravy')}
                  </span>
                </span>
              ) : undefined
            }
            massActionConfig={[
              {
                label: tr('MultiduplicationV3.keep', 'Ponechat'),
                secured: () => ({
                  id: 'mass-action-multiduplication-v3-keep-draft',
                  label: 'Keep draft rows',
                  description: 'Keep the selected rows of a bulk duplication draft',
                  visible: [
                    editFlag,
                    { type: 'state', label: `mode === 'DRAFT'`, satisfied: mode === 'DRAFT', description: 'Offered only while the bulk duplication is a draft, before it runs', byDesign: true },
                  ],
                }),
                disabled: () => action != null,
                onClick: () => {
                  const ids = (tableRef.current?.selectedObjects ?? []).map(r => r.id);
                  if (ids.length > 0) keepRows(ids, null);
                },
              },
            ]}
            rowExpansionTemplate={rowExpansionTemplate}
          />
        )
      ) : status === JobStatusEnum.ERROR ? null : (
        <>
          {detail.itemsWithIssues > 0 && (
            <>
              <div className={'flex flex-row gap-2 items-center font-medium'}>
                <SvgWarning className={'text-complementary-warning'} />
                {tr('MultiduplicationV3.sectionFailed', 'Neúspěšné')}
              </div>
              {failedTable}
              {job.failedCount > 0 && jobsRwFlag.granted && (
                <div>
                  <RingilButton
                    mode={'filled-rectangle'}
                    size={'big'}
                    label={tr('MultiduplicationV3.retryFailed', 'Zopakovat duplikaci neúspěšných')}
                    dataTestId={'multiduplication-v3-retry-failed'}
                    onClick={retry}
                    {...busy('retry')}
                  />
                </div>
              )}
            </>
          )}
          <div className={'font-medium'}>{tr('MultiduplicationV3.sectionCreated', 'Duplikované přepravy')}</div>
          {doneTable}
        </>
      )}

      {/* Deleting the draft lives on the header's trash button, which runs exactly this flow; the
          footer carried a second, identical control until the design dropped it. */}
      {mode === 'DRAFT' && editFlag.granted && (
        <div className={'flex flex-row gap-2 items-center'}>
          <RingilButton
            mode={'filled-rectangle'}
            size={'big'}
            label={tr('MultiduplicationV3.checkedCreate', 'Zkontrolováno, vytvořit přepravy')}
            onClick={execute}
            {...busy('execute')}
            dataTestId={'multiduplication-execute'}
          />
          <RingilButton
            mode={'outlined-rectangle'}
            size={'big'}
            label={tr('MultiduplicationV3.saveForLater', 'Uložit na později')}
            onClick={() => nav.navigate(leaveDraftUrl())}
          />
        </div>
      )}

      {isTerminal && <JobExpiryNote expiresAt={detail.expiresAt} />}

      <ConfirmDialog
        visible={deleteConfirmVisible}
        dialogTitle={tr('MultiduplicationV3.deleteTitle', 'Smazat seznam')}
        dialogMessage={tr('MultiduplicationV3.deleteConfirm', 'Opravdu smazat celý seznam?')}
        onAcceptButtonText={tr('MultiduplicationV3.deleteAccept', 'Smazat')}
        onCancelButtonText={tr('MultiduplicationV3.cancel', 'Zrušit')}
        onConfirm={() => {
          setDeleteConfirmVisible(false);
          deleteDraft();
        }}
        onCancel={() => setDeleteConfirmVisible(false)}
        dataTestId={'multiduplication-v3-delete-confirm'}
      />

      <ConfirmDialog
        visible={executeConfirmVisible}
        dialogTitle={tr('MultiduplicationV3.executeTitle', 'Vytvořit přepravy')}
        dialogMessage={tr('MultiduplicationV3.executeWithErrors', 'Některé řádky mají kolize nebo chyby. Pokračovat?')}
        onAcceptButtonText={tr('MultiduplicationV3.executeAccept', 'Pokračovat')}
        onCancelButtonText={tr('MultiduplicationV3.cancel', 'Zrušit')}
        onConfirm={() => {
          setExecuteConfirmVisible(false);
          runExecute();
        }}
        onCancel={() => setExecuteConfirmVisible(false)}
        dataTestId={'multiduplication-v3-execute-confirm'}
      />
    </div>
  );
};

export default MultiduplicationV3JobDetail;
